import { rtkFleetPartnersClient } from '@gm-commercial/rtk-clients';
import type {
  GmfOrganizationAddress,
  OrgEnrollmentResponseDto,
} from '@gm-commercial/rtk-clients/fleetPartners/types';

import { GmfProviderAccountStatus } from '~/common/models/gmf.model';
import { isFetchBaseQueryError } from '~/common/utils/rtk/rtk-query.utils';

const GMF_PROVIDER_CODE = 'GMF';

const LINK_STATUSES: readonly string[] = Object.values(
  GmfProviderAccountStatus
);

export type GmfLinkedAccount = {
  businessName?: string;
  dunsId?: string;
  address?: GmfOrganizationAddress[];
  fan?: string;
};

type EnrollmentResult = OrgEnrollmentResponseDto & {
  customData?: { orgDetails?: GmfLinkedAccount };
};

export type GmfProviderAccount = {
  /** `undefined` when the organization has no GMF enrollment. */
  status?: GmfProviderAccountStatus;
  /** Account fields from `customData.orgDetails`, when a row was returned. */
  account?: GmfLinkedAccount;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

const linkStatus = (
  status: string | undefined
): GmfProviderAccountStatus | undefined =>
  status && LINK_STATUSES.includes(status)
    ? (status as GmfProviderAccountStatus)
    : undefined;

/**
 * Reads the organization's GMF enrollment, the source of GMF link state.
 * A missing enrollment is a 404 and is a different answer from `DISABLED`.
 * An empty organization id skips the request.
 * @param organizationsId Organization to read.
 * @returns The enrollment status, its account fields, and request state.
 */
export const useGmfProviderAccount = (
  organizationsId: string
): GmfProviderAccount => {
  const { currentData, isFetching, isUninitialized, isError, error, refetch } =
    rtkFleetPartnersClient.useGetOrgEnrollmentQuery(
      {
        'cs-organization': organizationsId,
        providerCode: GMF_PROVIDER_CODE,
      },
      { skip: !organizationsId }
    );
  if (!organizationsId) {
    return {
      status: undefined,
      account: undefined,
      isLoading: false,
      isError: false,
      refetch,
    };
  }

  // `data` keeps the previous organization's row. A rejected read also leaves
  // that row on the current cache entry, so only a successful current result counts.
  const isMissingEnrollment =
    isError && isFetchBaseQueryError(error) && error.status === 404;
  const readFailed =
    (isError && !isMissingEnrollment) || currentData?.success === false;
  const enrollment =
    !readFailed && !isMissingEnrollment && currentData?.success
      ? (currentData.result as EnrollmentResult)
      : undefined;

  return {
    status: linkStatus(enrollment?.status),
    account: enrollment?.customData?.orgDetails,
    isLoading: isUninitialized || (isFetching && currentData === undefined),
    isError: readFailed,
    refetch,
  };
};
